import * as moment from 'moment-timezone';
import { DateUtilities, Utilities } from '@mitchell/react.component.ux';
import { DateFormatConstants, DAYS, MONTHS, LOCALE } from '../constants';
import { Language } from '../enums';
export function adjustDateToPstZero(date) {
    const newDate = new Date(date);
    const pstOffsetMinutes = 480;
    newDate.setHours(0, 0, 0, 0);
    const offsetMinutes = -newDate.getTimezoneOffset();
    newDate.setMinutes(offsetMinutes + pstOffsetMinutes);
    return newDate;
}
export function adjustUtcDateToStartOfDay(date) {
    const newDate = new Date(date);
    newDate.setUTCHours(0, 0, 0, 0);
    return newDate;
}
export function adjustUtcDateToEndOfDay(date) {
    const adjustedToEndOfDay = new Date(date);
    adjustedToEndOfDay.setUTCHours(23, 59, 59, 999);
    return adjustedToEndOfDay;
}
export function adjustDateWithTimeZero(date) {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
}
export function adjustDateToEndOfDay(date) {
    const adjustedToEndOfDay = new Date(date);
    adjustedToEndOfDay.setHours(23, 59, 59, 999);
    return adjustedToEndOfDay;
}
export function formatDateToStartOfDay(date, format) {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return formatDateTime(newDate, format);
}
export function formatDateToEndOfDay(date, format) {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return formatDateTime(newDate, format);
}
export function formatDate(dateValue, formatTo = DateFormatConstants.DATE_FORMAT_DD_MM_YYYY, formatFrom) {
    if (!dateValue) {
        return '';
    }
    return formatFrom ? moment(dateValue, formatFrom).format(formatTo) : moment(dateValue).format(formatTo);
}
export const formatDateToMonthYear = (date) => {
    return {
        month: date.getMonth(),
        year: date.getFullYear()
    };
};
export const formatDateTime = (date, dateFormat = DateFormatConstants.DATE_FORMAT_MM_DD_YYYY_HH_MM) => moment(date).format(dateFormat);
export const formatTodayDateWithTime = (inputDate) => {
    const { DATE_FORMAT_MM_DD_YYYY, DATE_FORMAT_MM_DD_YYYY_HH_MM } = DateFormatConstants;
    const { isToday } = DateUtilities;
    const dateFormat = isToday(inputDate) ? DATE_FORMAT_MM_DD_YYYY_HH_MM : DATE_FORMAT_MM_DD_YYYY;
    return moment(inputDate).format(dateFormat);
};
/**
 * Formats the specified date time into PST date time formatted string
 * There are some API calls that needs date to be in PST format like Vehicle options call and fails otherwise
 * @param inputDate Date to format
 */
export const formatAsPst = (inputDate, format = null) => {
    return moment.tz(inputDate, 'America/Los_Angeles').format(format);
};
/**
 * To subtract the numberof days from current date
 * @param numberOfDays number of days to subtract
 */
export const subtractDaysFromCurrentDate = (numberOfDays) => {
    return new Date(moment()
        .subtract(numberOfDays, DAYS)
        .calendar());
};
/**
 * To add the numberof days in current date
 * @param numberOfDays number of days to add
 */
export const addDaysToCurrentDate = (numberOfDays) => {
    return new Date(moment()
        .add(numberOfDays, DAYS)
        .calendar());
};
/**
 * To add the numberof months in current date
 * @param numberOfMonths number of months to add
 */
export const addMonthsToCurrentDate = (numberOfMonths) => {
    return new Date(moment()
        .add(numberOfMonths, MONTHS)
        .calendar());
};
/**
 * To convert Date to ISO String
 * @param inputDate date to convert to ISO String
 */
export const dateToISOString = (inputDate) => {
    return moment(inputDate).toISOString();
};
/**
 * To check whether two Dates are same.
 * @param firstInputDate first date to compare
 * @param secondInputDate second date to compare
 */
export const areSameDates = (firstInputDate, secondInputDate) => {
    return moment(firstInputDate).isSame(moment(secondInputDate));
};
/**
 * To get the string value of getTimezoneOffset.
 */
export const getTimezoneOffsetValue = () => {
    return new Date().getTimezoneOffset().toString();
};
/**
 * To get the string value getCurrentLocale
 */
export const getCurrentLocale = () => {
    const currentLanguage = Utilities.getLocale();
    const currentLocale = LOCALE.get(currentLanguage) || 'en-US';
    return currentLocale;
};
/**
 * To get the string value of getDateTimebyLocale
 */
export const getDateTimebyLocale = (inputDate) => {
    const currentLocale = getCurrentLocale();
    const date = inputDate.split('T')[0];
    const time = inputDate.split('T')[1].split('-')[0];
    const formattedDateTime = formatDate(`${date} ${time}`, currentLocale === Language.FRENCH_FRANCE || currentLocale === Language.FRENCH_CANADA
        ? DateFormatConstants.DATE_TIME_FORMAT_FRENCH
        : DateFormatConstants.MM_DD_YYYY_NON_MILITARY_TIME_FORMAT, DateFormatConstants.DATE_FORMAT_YYYY_MM_DD_HH_MM_SS);
    return formattedDateTime;
};
/**
 * Returns the number of days between two dates. Ignores time and will be negative if first date is after last date.
 * @param firstInputDate first date to compare
 * @param lastInputDate second date to compare
 */
export const getDaysBetweenDates = (firstInputDate, lastInputDate) => {
    const firstDate = new Date(firstInputDate.getFullYear(), firstInputDate.getMonth(), firstInputDate.getDate());
    const lastDate = new Date(lastInputDate.getFullYear(), lastInputDate.getMonth(), lastInputDate.getDate());
    if (areSameDates(firstDate, lastDate)) {
        return 1;
    }
    const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
    return Math.floor((lastDate.getTime() - firstDate.getTime()) / MILLISECONDS_PER_DAY);
};
/**
 * Adds a specified number of months to a given date.
 *
 * @param date - The source date to add months to
 * @param numberOfMonths - The number of months to add
 * @returns A new Date object representing the date with added months
 */
export const addMonthsToDate = (date, numberOfMonths) => {
    return new Date(moment(date)
        .add(numberOfMonths, 'months')
        .toDate());
};
/**
 * To subtract the number of days from current date (preserves current time)
 * @param numberOfDays number of days to subtract
 */
export const subtractDaysFromCurrentDateWithTime = (numberOfDays) => {
    return moment()
        .subtract(numberOfDays, DAYS)
        .toDate();
};
/**
 * Converts various date formats to a timestamp in milliseconds
 * @param dateValue - Date object, date string, or numeric timestamp
 * @returns number - Timestamp in milliseconds
 */
export const getDateTimeStamp = (dateValue) => {
    if (dateValue instanceof Date) {
        return dateValue.getTime();
    }
    if (typeof dateValue === 'string') {
        return new Date(dateValue).getTime();
    }
    return Number(dateValue);
};
export const normalizedDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    const dayOfWeek = normalized.getDay();
    if (dayOfWeek !== 0) {
        normalized.setDate(normalized.getDate() - dayOfWeek);
    }
    return normalized;
};
//# sourceMappingURL=DateUtils.js.map